import { Icon } from "common/components/icons";
import { isLoadedDefined, asBoolean, asString, loadedDefinedMap } from "common/components/component-helpers";
import {
    ActionSource,
    RenderEnvironment,
    Context,
    ForceValueProducer,
    asAction,
    EvaluatedMenuItem,
    MenuItemPurpose,
} from "common/components/types";
import { RemoteIcon } from "common/remote-icon-type";
import { safeURL } from "common/Support";
import { MenuItemStyle } from "common/components/menu-item-style";

export type ClickCallback = (handled: boolean) => void;

export interface MenuItem {
    title: string;
    icon: Icon | RemoteIcon;
    onClick?: ClickCallback;
    isEnabled?: boolean;
    // This is apparently only used for SMS URLs as of current.
    href?: string;
    style?: MenuItemStyle;
    purpose?: MenuItemPurpose;
}

export function getMenuItemValues(
    force: ForceValueProducer,
    menuItem: EvaluatedMenuItem,
    ctx: Context,
    env: RenderEnvironment
): MenuItem | undefined {
    const {
        isEnabled: isEnabledVP,
        isVisible: isVisibleVP,
        href: hrefVP,
        style: styleVP,
        purpose: purposeVP,
    } = menuItem;
    const titlePV = force(menuItem.title);
    const iconPV = force(menuItem.icon);
    const onTapValue = force(menuItem.onTap);

    if (!isLoadedDefined(titlePV) || !isLoadedDefined(iconPV)) return undefined;

    if (isVisibleVP !== undefined) {
        const isVisible = loadedDefinedMap(force(isVisibleVP), asBoolean);
        if (isVisible !== true) return undefined;
    }

    let isEnabled = true;
    if (isEnabledVP !== undefined) {
        const maybeIsEnabled = loadedDefinedMap(force(isEnabledVP), asBoolean);
        if (maybeIsEnabled === undefined) return undefined;
        isEnabled = maybeIsEnabled;
    }

    let href: string | undefined;
    if (hrefVP !== undefined) {
        const maybeHref = loadedDefinedMap(force(hrefVP), asString);
        if (maybeHref === undefined) return undefined;
        href = safeURL(maybeHref);
    }

    let style: MenuItemStyle = "default";
    if (styleVP !== undefined) {
        const maybeStyle = loadedDefinedMap(force(styleVP), asString);
        if (maybeStyle === undefined) return undefined;
        style = maybeStyle as MenuItemStyle;
    }

    let purpose: MenuItemPurpose | undefined;
    if (purposeVP !== undefined) {
        const maybePurpose = loadedDefinedMap(force(purposeVP), asString);
        if (maybePurpose === undefined) return undefined;
        purpose = maybePurpose as MenuItemPurpose;
    }

    return {
        title: asString(titlePV),
        icon: asString(iconPV) as Icon | RemoteIcon,
        onClick: () => asAction(onTapValue).run(ctx, env, ActionSource.Clickable),
        isEnabled,
        href,
        style,
        purpose,
    };
}
